import { useState } from 'react';
import { Sparkles, Volume2, Heart, Shield, BookOpen, Target, Lightbulb, FileText, Users, ClipboardList, Scale, Wrench } from 'lucide-react';
import { Button } from '../components/ui/button';
import SpEdKitLogo from '../components/SpEdKitLogo';
import AskSpEdKit from '../components/AskSpEdKit';

const HERO_IMAGE = "https://images.unsplash.com/photo-1759977064094-840dfc694bee?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwzfHxoYXBweSUyMGRpdmVyc2UlMjBlbGVtZW50YXJ5JTIwc3R1ZGVudHMlMjByZWFkaW5nJTIwdG9nZXRoZXIlMjB3YXJtJTIwbGlnaHR8ZW58MHx8fHwxNzcwODUyNjQ4fDA&ixlib=rb-4.1.0&q=85";

const LandingPage = () => {
  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const tools = [
    {
      icon: BookOpen,
      title: "Social Story Creator",
      description: "Generate personalized social stories with AI illustrations that look like your student",
      color: "from-primary to-primary-dark",
      textColor: "text-white"
    },
    {
      icon: Lightbulb,
      title: "UDL Strategy Hunter",
      description: "Get evidence-based accommodations and Universal Design for Learning strategies",
      color: "from-accent to-accent/80",
      textColor: "text-white"
    },
    {
      icon: Target,
      title: "IEP Goal Generator",
      description: "Create SMART goals aligned to standards with measurable objectives",
      color: "from-[#6366F1] to-[#8B5CF6]",
      textColor: "text-white"
    },
    {
      icon: FileText,
      title: "Present Levels Writer",
      description: "Generate comprehensive PLAAFP statements for IEP documentation",
      color: "from-[#EC4899] to-[#F472B6]",
      textColor: "text-white"
    },
    {
      icon: ClipboardList,
      title: "Worksheet Generator",
      description: "Create progress monitoring worksheets, practice activities, and task analysis guides",
      color: "from-[#14B8A6] to-[#0D9488]",
      textColor: "text-white"
    },
    {
      icon: Scale,
      title: "IEP Compliance Review",
      description: "Upload your drafted IEP for feedback on compliance with IDEA, CA Ed Code, and best practices",
      color: "from-[#8B5CF6] to-[#A855F7]",
      textColor: "text-white"
    },
    {
      icon: Wrench,
      title: "Support Staff Toolkit",
      description: "Generate ready-to-use accommodation tools like number lines, graphic organizers, and token boards by grade level",
      color: "from-orange-500 to-amber-500",
      textColor: "text-white"
    }
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="landing-page">
      {/* Header */}
      <header className="w-full px-6 py-4 flex justify-between items-center max-w-7xl mx-auto">
        <SpEdKitLogo className="w-10 h-10" textClassName="text-2xl" />
        <Button 
          onClick={handleLogin}
          className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-6 py-2 font-body font-medium shadow-soft transition-all active:scale-95"
          data-testid="header-login-btn"
        >
          Sign In
        </Button>
      </header>

      {/* Hero Section */}
      <section className="px-6 py-16 max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="animate-fade-in-up">
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Sparkles className="w-4 h-4" />
              <span>AI-Powered Special Education Tools</span>
            </div>
            <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl text-text-primary leading-tight mb-6">
              Your Complete <span className="text-primary">Special Education</span> Toolkit
            </h1>
            <p className="font-body text-text-secondary text-lg mb-8 max-w-lg">
              A complete toolkit for educators: create social stories, generate IEP goals, find UDL strategies, and write present levels — all powered by AI.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                onClick={handleLogin}
                className="bg-accent text-accent-foreground hover:bg-accent/90 rounded-full px-8 py-4 text-lg font-body font-semibold shadow-soft transition-all active:scale-95"
                data-testid="hero-get-started-btn"
              >
                Get Started
              </Button>
              <Button 
                variant="outline"
                className="border-2 border-primary text-primary hover:bg-primary/10 rounded-full px-8 py-4 text-lg font-body font-medium transition-all"
                data-testid="hero-learn-more-btn"
                onClick={() => document.getElementById('tools-section')?.scrollIntoView({ behavior: 'smooth' })}
              >
                Explore Tools
              </Button>
            </div>
          </div>
          <div className="animate-fade-in-up animation-delay-200 relative">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-secondary/40 rounded-3xl transform rotate-3"></div>
            <img 
              src={HERO_IMAGE} 
              alt="Happy children reading together" 
              className="relative rounded-3xl shadow-float w-full object-cover aspect-[4/3]"
            />
          </div>
        </div>
      </section>

      {/* Ask SpEdKit Section */}
      <section id="ask-spedkit" className="px-6 py-16 bg-gradient-to-br from-[#6366F1]/5 to-[#8B5CF6]/10">
        <div className="max-w-4xl mx-auto">
          <AskSpEdKit variant="full" />
        </div>
      </section>

      {/* Tools Hub Section */}
      <section id="tools-section" className="bg-secondary/30 px-6 py-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-text-primary mb-4">
              Core Tools for Educators
            </h2>
            <p className="font-body text-text-secondary max-w-2xl mx-auto">
              Everything you need to support diverse learners, all in one place
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            {tools.map((tool, index) => (
              <div 
                key={tool.title}
                onClick={handleLogin}
                className={`bg-gradient-to-br ${tool.color} rounded-2xl p-8 cursor-pointer hover:shadow-float transition-all duration-300 hover:-translate-y-1`}
                data-testid={`tool-card-${index}`}
              >
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <tool.icon className={`w-7 h-7 ${tool.textColor}`} />
                  </div>
                  <div>
                    <h3 className={`font-heading font-bold text-xl ${tool.textColor} mb-2`}>
                      {tool.title}
                    </h3>
                    <p className={`font-body ${tool.textColor}/80`}>
                      {tool.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="px-6 py-20 max-w-7xl mx-auto">
        <h2 className="font-heading font-bold text-3xl sm:text-4xl text-text-primary text-center mb-4">
          SpEdKit Features
        </h2>
        <p className="font-body text-text-secondary text-center mb-12 max-w-2xl mx-auto">
          Save time, ensure compliance, and focus on what matters most — your students
        </p>
        
        <div className="grid md:grid-cols-3 gap-8">
          <div className="bg-white rounded-2xl p-8 shadow-card hover:shadow-float transition-all duration-300 hover:-translate-y-1" data-testid="feature-card-personalized">
            <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center mb-6">
              <Sparkles className="w-7 h-7 text-primary" />
            </div>
            <h3 className="font-heading font-semibold text-xl text-text-primary mb-3">
              AI-Personalized Content
            </h3>
            <p className="font-body text-text-secondary">
              Every story, goal, and strategy is tailored to the individual student&apos;s needs, strengths, and interests.
            </p>
          </div>

          <div className="bg-white rounded-2xl p-8 shadow-card hover:shadow-float transition-all duration-300 hover:-translate-y-1" data-testid="feature-card-audio">
            <div className="w-14 h-14 bg-accent/10 rounded-xl flex items-center justify-center mb-6">
              <Volume2 className="w-7 h-7 text-accent" />
            </div>
            <h3 className="font-heading font-semibold text-xl text-text-primary mb-3">
              Multi-Modal Support
            </h3>
            <p className="font-body text-text-secondary">
              Text-to-speech, visual aids, downloadable PDFs, and email sharing — content accessible in multiple formats.
            </p>
          </div>

          <div className="bg-white rounded-2xl p-8 shadow-card hover:shadow-float transition-all duration-300 hover:-translate-y-1" data-testid="feature-card-accessibility">
            <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center mb-6">
              <Users className="w-7 h-7 text-primary" />
            </div>
            <h3 className="font-heading font-semibold text-xl text-text-primary mb-3">
              Classroom Ready
            </h3>
            <p className="font-body text-text-secondary">
              Organize by student, track progress over time, and share resources with parents and team members.
            </p>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="bg-secondary/30 px-6 py-20">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading font-bold text-3xl sm:text-4xl text-text-primary mb-6">
                Built on Evidence-Based Practices
              </h2>
              <p className="font-body text-text-secondary mb-6">
                Every tool incorporates proven frameworks and research-backed strategies:
              </p>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Heart className="w-4 h-4 text-primary" />
                  </div>
                  <span className="font-body text-text-secondary">Social-Emotional Learning (SEL) & CASEL frameworks</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Shield className="w-4 h-4 text-primary" />
                  </div>
                  <span className="font-body text-text-secondary">Universal Design for Learning (UDL) principles</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <BookOpen className="w-4 h-4 text-primary" />
                  </div>
                  <span className="font-body text-text-secondary">Carol Gray&apos;s Social Stories™ methodology</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Target className="w-4 h-4 text-primary" />
                  </div>
                  <span className="font-body text-text-secondary">IDEA-compliant IEP documentation standards</span>
                </li>
              </ul>
            </div>
            <div className="bg-gradient-to-br from-primary/5 to-secondary/30 rounded-3xl p-8">
              <blockquote className="font-body text-text-primary text-lg italic mb-4">
                &quot;When we presume competence and provide the right supports, every child can thrive. SpEdKit helps educators focus on strengths while building understanding and confidence.&quot;
              </blockquote>
              <p className="font-body text-text-secondary text-sm">
                — Adapted from neurodiversity-affirming practices endorsed by the Autistic Self Advocacy Network
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-primary px-6 py-16">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-heading font-bold text-3xl sm:text-4xl text-white mb-4">
            Ready to Transform Your Practice?
          </h2>
          <p className="font-body text-white/80 mb-8">
            Join educators who are saving hours each week while creating better outcomes for their students.
          </p>
          <Button 
            onClick={handleLogin}
            className="bg-white text-primary hover:bg-white/90 rounded-full px-10 py-4 text-lg font-body font-semibold shadow-float transition-all active:scale-95"
            data-testid="cta-get-started-btn"
          >
            Start Using SpEdKit
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-background-alt px-6 py-8">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <SpEdKitLogo className="w-6 h-6" textClassName="text-base" />
          <div className="text-center md:text-right">
            <p className="font-body text-text-muted text-sm">
              © 2026 SpEdKit. Empowering educators to support every learner.
            </p>
            <p className="font-body text-text-muted text-sm mt-1">
              Questions about this site? <a href="mailto:Jennifer.Blake@ousd.org" className="text-primary hover:underline">Jennifer.Blake@ousd.org</a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
